import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { ACTIVE_NETWORK, getAddEthereumChainParams } from '../config/networks';
import { detectInjected, isMobile, buildTrustDeepLink } from '../utils/walletDetect';
import { useNotify } from './NotificationContext';

const WalletContext = createContext(null);

const STORAGE_KEY = 'gx_wallet_pref';
const TARGET_CHAIN_ID = ACTIVE_NETWORK.chainId;
const TARGET_CHAIN_HEX = ACTIVE_NETWORK.chainIdHex;

export const WalletProvider = ({ children }) => {
  const notify = useNotify();
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null);   // ethers Web3Provider
  const [signer, setSigner] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [walletName, setWalletName] = useState(null);
  const [networkOk, setNetworkOk] = useState(false);
  const [switching, setSwitching] = useState(false);

  const rawProviderRef = useRef(null); // injected EIP-1193 provider

  // ---------- Refresh signer/provider state from rawProvider (no reload) ----------
  const syncFromRaw = async (rawProv) => {
    if (!rawProv) return;
    const web3Provider = new ethers.providers.Web3Provider(rawProv, 'any');
    const accounts = await web3Provider.listAccounts();
    const net = await web3Provider.getNetwork();
    setProvider(web3Provider);
    setSigner(accounts.length ? web3Provider.getSigner() : null);
    setAccount(accounts[0] || null);
    setChainId(net.chainId);
    setNetworkOk(net.chainId === TARGET_CHAIN_ID);
  };

  // ---------- Try to switch / add target network silently ----------
  const ensureNetwork = async (rawProv) => {
    if (!rawProv) return false;
    setSwitching(true);
    try {
      await rawProv.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: TARGET_CHAIN_HEX }]
      });
      await syncFromRaw(rawProv);
      setSwitching(false);
      return true;
    } catch (err) {
      // 4902 = chain not added; some wallets use -32603
      if (err && (err.code === 4902 || err.code === -32603 || /unrecognized chain/i.test(err.message || ''))) {
        try {
          await rawProv.request({
            method: 'wallet_addEthereumChain',
            params: [getAddEthereumChainParams()]
          });
          await syncFromRaw(rawProv);
          setSwitching(false);
          return true;
        } catch (addErr) {
          notify.error('Network', 'Please approve the network add request.');
          setSwitching(false);
          return false;
        }
      }
      if (err && err.code === 4001) {
        notify.warn('Network', 'Network switch was declined.');
      } else {
        notify.error('Network', err?.message || 'Failed to switch network');
      }
      setSwitching(false);
      return false;
    }
  };

  // ---------- Connect ----------
  const connect = async (preferred = 'auto') => {
    setConnecting(true);
    try {
      const det = detectInjected(preferred);
      if (!det) {
        if (isMobile()) {
          notify.info('Open in wallet', 'Redirecting to Trust Wallet...');
          window.location.href = buildTrustDeepLink();
          return;
        }
        notify.error('No wallet', 'Install Trust Wallet, MetaMask, or another web3 wallet');
        setConnecting(false);
        return;
      }

      const rawProv = det.provider;
      rawProviderRef.current = rawProv;
      setWalletName(det.name);

      const accounts = await rawProv.request({ method: 'eth_requestAccounts' });
      if (!accounts || !accounts.length) {
        notify.error('Connect', 'No accounts returned');
        setConnecting(false);
        return;
      }

      // Read current chain
      const currentHex = await rawProv.request({ method: 'eth_chainId' });
      const currentId = parseInt(currentHex, 16);
      if (currentId !== TARGET_CHAIN_ID) {
        await ensureNetwork(rawProv);
      }
      await syncFromRaw(rawProv);

      // Persist preference
      try { localStorage.setItem(STORAGE_KEY, preferred); } catch {}

      // Bind listeners (once per provider)
      bindListeners(rawProv);

      notify.success('Connected', `${det.name}`);
    } catch (err) {
      if (err && err.code === 4001) {
        notify.warn('Connect', 'Request rejected');
      } else {
        notify.error('Connect', err?.message || 'Failed to connect');
      }
    } finally {
      setConnecting(false);
    }
  };

  // ---------- Disconnect (local only — wallets don't actually disconnect) ----------
  const disconnect = () => {
    setAccount(null);
    setSigner(null);
    setProvider(null);
    setChainId(null);
    setWalletName(null);
    setNetworkOk(false);
    rawProviderRef.current = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    notify.info('Disconnected', 'Wallet disconnected');
  };

  // ---------- Provider listeners ----------
  const bindListeners = (rawProv) => {
    if (!rawProv || !rawProv.on || rawProv._gxBound) return;
    rawProv._gxBound = true;

    rawProv.on('accountsChanged', (accs) => {
      if (!accs || !accs.length) {
        setAccount(null);
        setSigner(null);
        notify.warn('Wallet', 'Disconnected from wallet');
      } else {
        setAccount(accs[0]);
        // Re-create signer
        const web3Provider = new ethers.providers.Web3Provider(rawProv, 'any');
        setProvider(web3Provider);
        setSigner(web3Provider.getSigner());
      }
    });

    rawProv.on('chainChanged', (hex) => {
      const id = parseInt(hex, 16);
      setChainId(id);
      setNetworkOk(id === TARGET_CHAIN_ID);
      // Refresh provider WITHOUT page reload
      const web3Provider = new ethers.providers.Web3Provider(rawProv, 'any');
      setProvider(web3Provider);
      if (rawProv.selectedAddress || account) {
        setSigner(web3Provider.getSigner());
      }
      // If wrong network — try silent switch
      if (id !== TARGET_CHAIN_ID) {
        ensureNetwork(rawProv);
      }
    });

    rawProv.on('disconnect', () => {
      setAccount(null);
      setSigner(null);
      setProvider(null);
    });
  };

  // ---------- Auto-reconnect on mount ----------
  useEffect(() => {
    const tryEager = async () => {
      try {
        const pref = localStorage.getItem(STORAGE_KEY);
        const det = detectInjected(pref || 'auto');
        if (!det) return;
        const rawProv = det.provider;
        const accounts = await rawProv.request({ method: 'eth_accounts' });
        if (accounts && accounts.length) {
          rawProviderRef.current = rawProv;
          setWalletName(det.name);
          const hex = await rawProv.request({ method: 'eth_chainId' });
          const id = parseInt(hex, 16);
          if (id !== TARGET_CHAIN_ID) {
            await ensureNetwork(rawProv);
          }
          await syncFromRaw(rawProv);
          bindListeners(rawProv);
        }
      } catch {}
    };
    tryEager();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Public switch network helper ----------
  const switchNetwork = async () => {
    if (!rawProviderRef.current) return false;
    return ensureNetwork(rawProviderRef.current);
  };

  return (
    <WalletContext.Provider
      value={{
        account,
        chainId,
        provider,
        signer,
        connect,
        disconnect,
        connecting,
        switching,
        walletName,
        networkOk,
        targetChainId: TARGET_CHAIN_ID,
        switchNetwork
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be inside WalletProvider');
  return ctx;
};
